import {
    CompletionCallMetadata,
    getNextIdForPrefix,
    invalidOutlineRetryAmount,
    getContentRecursively,
    getSectionNameByPointer,
    textComponentType
} from './aiExtensionContent'
import {fetchLinkGenerationFromPromptHub} from './aiExtensionUtils'
import {getReportableFromError, ReportableError} from '@wix/document-manager-utils'
import {
    aiContentBadJsonErrorMessage,
    aiContentBadJsonErrorType,
    aiContentUnexpectedError,
    aiContentUnexpectedErrorMessage,
    aiLinkGenerationInteraction,
    aiLinkGenerationInteractionFetchLinks,
    aiLinkGenerationInteractionGenerateLinksDataItems,
    aiLinkGenerationInteractionGetPageOutline,
    aiLinkGenerationRequestErrorMessage,
    aiLinkGenerationRequestErrorType,
    aiLinkGenerationResultConsecutiveErrorMessage,
    aiLinkGenerationResultConsecutiveErrorType,
    nonApplicableParamsVersion,
    promptHubLinkGenerationPromptId
} from './constants'
import type {Outline, OutlineWithIdMap} from './aiContentExtension'
import * as constants from '../../constants/constants'
import {DATA_TYPES, LINKABLE_BUTTONS, MASTER_PAGE_ID, VIEW_MODES} from '../../constants/constants'
import type {CompRef, Pointer} from '@wix/document-services-types'
import _ from 'lodash'
import type {CoreLogger, CreateExtArgs, EnvironmentContext, ExtensionAPI} from '@wix/document-manager-core'
import type {StructureExtensionAPI} from '../structure'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {PageExtensionAPI} from '../page'
import type {RoutersExtensionAPI} from '../routers'

export interface LinkOutlineTargetsWithIdMap extends OutlineWithIdMap {
    targets: LinkTargets
    currentPage: string
}

export interface LinkTargets {
    sections?: Outline
    anchors?: Outline
    pages: string[]
}
interface LinkOutlineResult {
    [key: string]: {
        elementType: string
        sectionName?: string
        pageName?: string
    }
}
interface LinkGenerationValidationResult {
    isValid: boolean
    reason?: string
}
export interface LinkGenerationOutlineSuggestionResult extends OutlineWithIdMap {
    validationResults: LinkGenerationValidationResult
    originalOutline: Outline
    originalTargets: LinkTargets
    defaultInnerRouterMap?: Record<string, any>
    pageId: string
    retries: number
    completionMetadata: {
        promptsVersion: string
        gptParamsVersion: string
    }
}

export interface LinkGenerationOutlineSuggestionResultForPages extends OutlineWithIdMap {
    validationResults: LinkGenerationValidationResult
    originalOutline: Outline
    originalTargets: LinkTargets
    pageId: string
    retries: number
    completionMetadata: {
        promptsVersion: string
        gptParamsVersion: string
    }
}
export const reportLinkGenerationBIError = (
    logger: CoreLogger,
    errorType: string,
    message: string,
    result: LinkOutlineResult,
    pageId: string,
    validationResults: LinkGenerationValidationResult
) => {
    const error = new ReportableError({
        errorType,
        message,
        extras: {
            pageId,
            suggestedOutline: result,
            validationResults
        }
    })
    logger.captureError(error)
}

const getLinkTargets = (
    createExtArgs: CreateExtArgs,
    idMap: Record<string, string>,
    typeCounter: Record<string, number>,
    sectionMap: Record<string, string>
): LinkTargets => {
    const {extensionAPI, pointers} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {page: pageExt} = extensionAPI as PageExtensionAPI
    const {routers} = extensionAPI as RoutersExtensionAPI

    const getPageSections = (pageId: string): Record<string, string> | undefined => {
        const pageSections = pointers.structure.getComponentsByTypeForPage(constants.COMP_TYPES.SECTION, pageId)
        if (!pageSections.length) return
        return pageSections.reduce<Record<string, string>>((sections, sectionPointer) => {
            const anchorData = dataModel.components.getItem(sectionPointer, constants.DATA_TYPES.anchors)
            const name = sectionMap[sectionPointer.id] ?? getSectionNameByPointer(extensionAPI, sectionPointer)
            const nameWithCounter = getNextIdForPrefix('section', typeCounter)
            idMap[nameWithCounter] = anchorData.id
            sections[nameWithCounter] = name
            return sections
        }, {})
    }

    return pageExt.getAllPagesIds(false).reduce<LinkTargets>(
        (linkTargets, pageId) => {
            const pointer = pointers.getPointer(pageId, VIEW_MODES.DESKTOP)
            const {pageUriSEO} = dataModel.components.getItem(pointer, constants.DATA_TYPES.data)
            idMap[pageUriSEO] = pageId
            const isDynamic = routers.getRouterByPage(pageId)
            linkTargets.pages.push(pageUriSEO)
            if (!isDynamic) {
                const pageSections = getPageSections(pageId)
                if (pageSections) (linkTargets.sections ??= {})[pageUriSEO] = pageSections
            }

            return linkTargets
        },
        {pages: []}
    )
}

export const getPageLinksOutline = (createExtArgs: CreateExtArgs, pageId: string): LinkOutlineTargetsWithIdMap => {
    const {pointers, extensionAPI} = createExtArgs
    const {components} = extensionAPI as StructureExtensionAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const pagePointer = pointers.structure.getComponentById(pageId, VIEW_MODES.DESKTOP)

    const outline: Outline = {}
    const typeCounter: Record<string, number> = {}
    const idMap: Record<string, string> = {}
    const sectionsMap: Record<string, string> = {}

    const pointerPredicate = (pointer: Pointer) => {
        const dataItem = dataModel.components.getItem(pointer, constants.DATA_TYPES.data)
        const componentType = components.getComponentType(pointer)
        return (LINKABLE_BUTTONS.includes(componentType) && !dataItem.link) || componentType === textComponentType
    }

    getContentRecursively(createExtArgs, pagePointer, outline, typeCounter, idMap, undefined, pointerPredicate)

    Object.entries(idMap).forEach(([key, val]) => (sectionsMap[val] = key))
    const pageData = dataModel.getItem(pageId, constants.DATA_TYPES.data, MASTER_PAGE_ID)
    return {
        outline,
        idMap,
        targets: getLinkTargets(createExtArgs, idMap, typeCounter, sectionsMap),
        currentPage: pointers.structure.isMasterPage(pagePointer) ? MASTER_PAGE_ID : pageData.pageUriSEO
    }
}

const validateLinkGenerationOutlineSuggestion = (
    resultOutline: LinkOutlineResult,
    targets: LinkTargets,
    idMap: Record<string, string>
) => {
    const possibleElementsTypes = ['page', 'section']
    for (const [buttonId, {elementType, pageName, sectionName}] of Object.entries(resultOutline)) {
        const buttonCompId = idMap[buttonId]
        if (!buttonCompId) {
            break
        }
        if (!elementType || !_.includes(possibleElementsTypes, elementType)) {
            return {isValid: false, reason: 'wrong or missing element type', value: elementType}
        }
        if (!pageName && !sectionName) {
            return {isValid: false, reason: 'should contain pageName or section name'}
        }
        if (pageName && !_.includes(targets.pages, pageName)) {
            return {isValid: false, reason: 'wrong page name', value: pageName}
        }
        if (sectionName && !idMap.hasOwnProperty(sectionName)) {
            return {isValid: false, reason: 'section or anchor does not exist', value: sectionName}
        }
    }
    return {isValid: true}
}

export const getSuggestedLinksToInject = async (
    environmentContext: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    pageId: string
): Promise<LinkGenerationOutlineSuggestionResult> => {
    const {serverFacade} = environmentContext
    const {coreConfig} = createExtArgs
    const {logger} = coreConfig

    logger.interactionStarted(aiLinkGenerationInteraction, {
        extras: {
            suggestionPurpose: 'linkGenerationInjection',
            pageId
        }
    })
    logger.interactionStarted(aiLinkGenerationInteractionGetPageOutline, {
        extras: {
            pageId
        }
    })
    const content = getPageLinksOutline(createExtArgs, pageId)
    logger.interactionEnded(aiLinkGenerationInteractionGetPageOutline, {
        extras: {
            pageId
        }
    })

    const {outline, idMap, targets, currentPage} = content
    let resultOutline: LinkOutlineResult | null = null

    let apiCallCount = 0
    let lastError: Error | null = null
    const callsMetadata: CompletionCallMetadata[] = []
    let validationResults: LinkGenerationValidationResult

    try {
        while (apiCallCount <= invalidOutlineRetryAmount) {
            logger.interactionStarted(aiLinkGenerationInteractionFetchLinks, {
                extras: {
                    apiCallCount,
                    pageId
                }
            })
            const completionResponse = await fetchLinkGenerationFromPromptHub(
                serverFacade,
                pageId,
                outline,
                targets,
                currentPage
            )
            logger.interactionEnded(aiLinkGenerationInteractionFetchLinks, {
                extras: {
                    apiCallCount,
                    pageId
                }
            })
            const completionText = completionResponse.generatedTexts[0]
            apiCallCount += 1

            try {
                resultOutline = JSON.parse(completionText!)
            } catch (e) {
                callsMetadata.push({
                    isBadJson: true
                })
                lastError = new ReportableError({
                    errorType: aiContentBadJsonErrorType,
                    message: aiContentBadJsonErrorMessage,
                    extras: {
                        completionText,
                        pageId
                    }
                })
                continue
            }
            validationResults = validateLinkGenerationOutlineSuggestion(resultOutline!, targets, idMap)
            if (validationResults.isValid) {
                break
            } else if (apiCallCount > 1) {
                reportLinkGenerationBIError(
                    logger,
                    aiLinkGenerationResultConsecutiveErrorType,
                    aiLinkGenerationResultConsecutiveErrorMessage,
                    resultOutline!,
                    pageId,
                    validationResults
                )
            } else {
                reportLinkGenerationBIError(
                    logger,
                    aiLinkGenerationRequestErrorType,
                    aiLinkGenerationRequestErrorMessage,
                    resultOutline!,
                    pageId,
                    validationResults
                )
            }
        }
        if (!resultOutline) {
            throw (
                lastError ??
                new ReportableError({
                    errorType: aiContentUnexpectedError,
                    message: aiContentUnexpectedErrorMessage
                })
            )
        }
        return {
            outline: resultOutline!,
            idMap,
            validationResults: validationResults!,
            originalOutline: outline,
            originalTargets: targets,
            retries: apiCallCount - 1,
            pageId,
            completionMetadata: {
                gptParamsVersion: nonApplicableParamsVersion,
                promptsVersion: promptHubLinkGenerationPromptId
            }
        }
    } catch (e: any) {
        // ensure that tokenUsage is always in the error
        const reportedError = getReportableFromError(e, {
            errorType: e.errorType ?? aiContentUnexpectedError,
            tags: {
                aiContentInjection: true
            }
        })
        logger.captureError(reportedError)

        throw reportedError
    } finally {
        logger.interactionEnded(aiLinkGenerationInteraction, {
            extras: {
                suggestionPurpose: 'linkGenerationInjection',
                pageId,
                outline,
                targets,
                resultOutline,
                completionCalls: callsMetadata
            }
        })
    }
}
const addLinkDataItem = (extensionAPI: ExtensionAPI, compId: string, linkData: Record<string, any>) => {
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const compRef = {id: compId, type: 'DESKTOP'}
    dataModel.components.addItem(compRef, 'data', {link: linkData})
}

export const generateLinksForOutline = (
    createExtArgs: CreateExtArgs,
    outlineWithIds: LinkGenerationOutlineSuggestionResult,
    pageId: string
) => {
    const {coreConfig} = createExtArgs
    const {logger} = coreConfig

    logger.interactionStarted(aiLinkGenerationInteractionGenerateLinksDataItems, {
        extras: {
            pageId
        }
    })
    const {dal, extensionAPI, pointers} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {components} = extensionAPI as StructureExtensionAPI
    const {routers} = extensionAPI as RoutersExtensionAPI

    const {idMap, outline, defaultInnerRouterMap} = outlineWithIds
    for (const buttonId of Object.keys(outline)) {
        const buttonCompId = idMap[buttonId]
        if (!buttonCompId) {
            continue
        }
        const compPointer = pointers.getPointer(buttonCompId, VIEW_MODES.DESKTOP) as CompRef
        const componentType = components.getComponentType(compPointer)
        if (!LINKABLE_BUTTONS.includes(componentType)) {
            continue
        }
        const buttonLinkData = outline[buttonId]
        switch (buttonLinkData.elementType) {
            case 'page':
                const pageIdToLink = idMap[buttonLinkData.pageName]
                if (!pageIdToLink) {
                    continue
                }
                const isDynamic = routers.getRouterByPage(pageIdToLink)
                if (isDynamic) {
                    if (defaultInnerRouterMap?.[pageIdToLink]) {
                        const pageLinkRawData = dal.schema.createItemAccordingToSchema(
                            'DynamicPageLink',
                            DATA_TYPES.data
                        )
                        _.assign(pageLinkRawData, defaultInnerRouterMap?.[pageIdToLink])
                        addLinkDataItem(extensionAPI, buttonCompId, pageLinkRawData)
                    }
                    continue
                }
                const pageLinkRawData = dal.schema.createItemAccordingToSchema('PageLink', DATA_TYPES.data)
                _.assign(pageLinkRawData, {pageId: `#${pageIdToLink}`})
                addLinkDataItem(extensionAPI, buttonCompId, pageLinkRawData)
                break
            case 'section':
                const anchorDataId = idMap[buttonLinkData.sectionName]
                if (!anchorDataId) {
                    break
                }
                const anchorDataPageId = pointers.data.getPageIdOfData({id: anchorDataId, type: DATA_TYPES.anchors})
                if (!anchorDataPageId) {
                    break
                }
                const isDynamicPage = routers.getRouterByPage(anchorDataPageId)
                if (isDynamicPage) {
                    break
                }
                const anchorData = dataModel.getItem(anchorDataId, DATA_TYPES.anchors, anchorDataPageId)
                const anchorLinkRawData = dal.schema.createItemAccordingToSchema('AnchorLink', DATA_TYPES.data)
                _.assign(anchorLinkRawData, {
                    pageId: `#${anchorDataPageId}`,
                    anchorDataId: `#${anchorDataId}`,
                    anchorName: anchorData.name
                })
                addLinkDataItem(extensionAPI, buttonCompId, anchorLinkRawData)
                break
        }
    }
    logger.interactionEnded(aiLinkGenerationInteractionGenerateLinksDataItems, {
        extras: {
            pageId
        }
    })
}
